import { format, subDays } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { OrdemServicoFilters } from '@/types';
import type { TaskAgenda } from '@/types/taskAgenda';
import { clampTaskAgendaRangeToCurrentMonthSp, nextPayloadWithAutoSlot } from '@/services/taskAgenda/taskAgendaSchedule';
import { syncWithApi } from '@/utils/ordensServico/ordensServicoApiSync';

export type RunAgendaOsSyncOptions = {
  automaticSlotKey?: string;
};

function buildOsSyncFilters(agenda: TaskAgenda): OrdemServicoFilters {
  const raw = agenda.payload?.syncDaysRange;
  const days = typeof raw === 'number' && raw > 0 && raw <= 365 ? raw : 14;
  const end = new Date();
  const start = subDays(end, days);
  return clampTaskAgendaRangeToCurrentMonthSp(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
}

/**
 * Sincronização de OS pela API para uma linha da agenda; grava execuções em task_agenda_runs e last_run_*.
 */
export async function runAgendaOrdensServicoSync(
  agenda: TaskAgenda,
  imposto: number,
  options?: RunAgendaOsSyncOptions
): Promise<void> {
  const automaticSlotKey = options?.automaticSlotKey;
  const nextPayload = () => nextPayloadWithAutoSlot(agenda, automaticSlotKey);
  const filters = buildOsSyncFilters(agenda);

  const { data: runRow, error: insErr } = await supabase
    .from('task_agenda_runs')
    .insert({
      agenda_id: agenda.id,
      status: 'running',
      summary: null,
      error_message: null,
    })
    .select('id')
    .single();

  if (insErr || !runRow?.id) {
    const msg = insErr?.message || 'insert sem id';
    console.error('[task_agenda] Falha ao registrar execução:', insErr);
    throw new Error(`Não foi possível registrar a execução na agenda: ${msg}`);
  }

  const runId = runRow.id as string;

  try {
    const result = await syncWithApi(filters, imposto);
    const summary = `Páginas: ${result.pagesProcessed}; novas: ${result.newOrders}; ignoradas: ${result.skippedOrders}`;
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'success',
        summary,
        error_message: null,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'success',
        last_run_summary: summary,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'error',
        summary: null,
        error_message: msg,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'error',
        last_run_summary: msg,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
    throw e;
  }
}
